import { useState, useContext, useEffect } from "react";
import { useFormik, Form, FormikProvider } from "formik";
import * as Yup from "yup";
import {
  Grid,
  Button,
  Dialog,
  Select,
  MenuItem,
  TextField,
  Typography,
  FormControl,
  DialogContent,
} from "@mui/material";

//Import from project
import "../../Style/dialogstyle.scss";
import { useMutation } from "@apollo/client";
import { AuthContext } from "../../Context/AuthContext";
import { DialogTitleComp } from "../../Utils/Component";
import {
  CREATE_VILLAGE,
  UPDATE_VILLAGE,
} from "../../Schema/CapitalProvince";

export default function VillageForm({
  open,
  editData,
  setRefetch,
  dialogTitle,
  handleClose,
  hideBackdrop,
  communeSangkatId,
  districtMunicipalitiesId

}) {
  // Change Language
  const { t, setAlert } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);

  const queryParams = new URLSearchParams(window.location.search);
  const provinceId = queryParams.get("provinceId");

  const [createVillage] = useMutation(CREATE_VILLAGE, {
    onCompleted: ({ createVillage }) => {
      setLoading(false);
      if (createVillage?.isSuccess === true) {
        setAlert(true, "success", createVillage?.message);
        handleClose();
        resetForm();
        setRefetch();
      } else {
        setAlert(true, "error", createVillage?.message);
      }
    },
    onError: ({ message }) => {
      setLoading(false);
      setAlert(true, "error", { messageKh: message, messageEn: message });
    },
  });

  const [updateVillage] = useMutation(UPDATE_VILLAGE, {
    onCompleted: ({ updateVillage }) => {
      setLoading(false);
      if (updateVillage?.isSuccess === true) {
        setAlert(true, "success", updateVillage?.message);
        handleClose();
        resetForm();
        setRefetch();
      } else {
        setAlert(true, "error", updateVillage?.message);
      }
    },
    onError: ({ message }) => {
      setLoading(false);
      setAlert(true, "error", { messageKh: message, messageEn: message });
    },
  });

  //fromik
  const CheckValidation = Yup.object().shape({
    village_code: Yup.string().required(t("required")),
    village_name_en: Yup.string().required(t("required")),
    village_name_kh: Yup.string().required(t("required")),
  });

  const formik = useFormik({
    initialValues: {
      village_code: "",
      village_name_en: "",
      village_name_kh: "",
    },

    validationSchema: CheckValidation,
    onSubmit: async (values) => {
      setLoading(true);
      if (dialogTitle === "Create") {
        createVillage({
          variables: {
            input: {
              ...values,

              commune_sangkat_id: communeSangkatId,
              capical_provinces_id: provinceId,
              district_municipalities_id: districtMunicipalitiesId,
            },
          },
        });
      } else {
        updateVillage({
          variables: {
            id: editData?._id,
            input: {
              ...values,
              commune_sangkat_id: communeSangkatId,
              capical_provinces_id: provinceId,
              district_municipalities_id: districtMunicipalitiesId,
            },
          },
        });
      }
    },
  });

  const {
    errors,
    touched,
    handleSubmit,
    setFieldValue,
    getFieldProps,
    resetForm,
  } = formik;

  useEffect(() => {
    if (editData) {
      setFieldValue("village_code", editData?.village_code);
      setFieldValue(
        "village_name_en",
        editData?.village_name_en
      );
      setFieldValue(
        "village_name_kh",
        editData?.village_name_kh
      );
    }
  }, [editData, open]);

  return (
    <Dialog
      fullWidth
      open={open}
      maxWidth="xs"
      className="dialog-container" 
      hideBackdrop={hideBackdrop}
    >
      <DialogTitleComp
        title={
          dialogTitle === "Create"
            ? t("modal_title_create_village")
            : t("modal_title_update_village")
        }
        classTitle={"dialog-title"}
        classIcon={"close-icon"}
        handleClose={handleClose}
      />

      <DialogContent dividers>
        <FormikProvider value={formik}>
          <Form noValidate autoComplete="off" onSubmit={handleSubmit}>
            <Grid container rowSpacing={2} columnSpacing={3}>
              <Grid item xs={12}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_code")}
                </Typography>
                <TextField
                  className="text-field"
                  size="small"
                  fullWidth
                  placeholder={t("thead_code")}
                  {...getFieldProps("village_code")}
                  error={Boolean(
                    touched.village_code &&
                    errors.village_code
                  )}
                  helperText={
                    touched.village_code &&
                    errors.village_code
                  }
                />
              </Grid>


              <Grid item xs={12}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_name")}(KH)
                </Typography>
                <TextField
                  className="text-field"
                  size="small"
                  fullWidth
                  placeholder={t("thead_name")}
                  {...getFieldProps("village_name_kh")}
                  error={Boolean(
                    touched.village_name_kh &&
                    errors.village_name_kh
                  )}
                  helperText={
                    touched.village_name_kh &&
                    errors.village_name_kh
                  }
                />
              </Grid>

              <Grid item xs={12}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_name")}(ENG)
                </Typography>
                <TextField
                  className="text-field"
                  size="small"
                  fullWidth
                  placeholder={t("thead_name")}
                  {...getFieldProps("village_name_en")}
                  error={Boolean(
                    touched.village_name_en &&
                    errors.village_name_en
                  )}
                  helperText={
                    touched.village_name_en &&
                    errors.village_name_en
                  }
                />
              </Grid>

              <Grid item xs={12}>
                {loading ? (
                  <Button className={t("class-btn-create")} fullWidth>
                    {t("loading")}
                  </Button>
                ) : (
                  <Button
                    className={t("class-btn-create")}
                    fullWidth
                    onClick={handleSubmit}
                  >
                    {dialogTitle === "Create" ? t("create") : t("update")}
                  </Button>
                )}
              </Grid>
            </Grid>
          </Form>
        </FormikProvider>
      </DialogContent>
    </Dialog>
  );
}
